import React, { useMemo } from 'react';
import { GeoJSONSourceOptions } from 'mapbox-gl';

import { color } from '@strava/ui-tokens/js';

import { Layer } from './Layer';
import { Source } from './Source';
import { getCircleCoords } from './utils';

interface PolygonCircleStyle {
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  strokeOffset?: number;
}

export interface PolygonCircleProps {
  /**
   * Unique ID of the layer.
   */
  id: string;

  /**
   * Latitude of the center of the circle.
   */
  lat: number;

  /**
   * Longitude  of the center of the circle.
   */
  lng: number;

  /**
   * Radius of the circle in meters.
   */
  radius: number;

  /**
   * Style object of the circle.
   */
  style: PolygonCircleStyle;
}

export const PolygonCircle = ({
  id,
  lat,
  lng,
  radius,
  style: customStyle
}: PolygonCircleProps) => {
  const style: PolygonCircleStyle = {
    fillColor: color.colorCoreAsphalt,
    fillOpacity: 1,
    strokeColor: color.colorCoreAsphalt,
    strokeOpacity: 1,
    strokeWeight: 0,
    strokeOffset: 0,
    ...customStyle
  };

  const data = useMemo<GeoJSONSourceOptions['data']>(
    () => ({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [getCircleCoords([lng, lat], radius)]
          },
          properties: {
            lat,
            lng
          }
        }
      ]
    }),
    [lng, lat, radius]
  );

  return (
    <Source
      id={`${id}-source`}
      type="geojson"
      data={data}
      layers={({ sourceId }) => (
        <>
          <Layer
            id={id}
            type="fill"
            source={sourceId}
            paint={{
              'fill-color': style.fillColor,
              'fill-opacity': style.fillOpacity
            }}
          />
          <Layer
            id={`${id}-outline`}
            type="line"
            source={sourceId}
            paint={{
              'line-color': style.strokeColor,
              'line-opacity': style.strokeOpacity,
              'line-width': style.strokeWeight,
              'line-offset': style.strokeOffset
            }}
          />
        </>
      )}
    />
  );
};
